import { Check } from "lucide-react";
import { cn } from "@/v2/lib/utils";
import { NeedHelpCard } from "@/v2/pages/FavoritesManager/components/right-rail/NeedHelpCard";
import { SidebarLegalFooter } from "@/v2/components/SidebarLegalFooter";
import type { GiftStep, StepInfo } from "../types";

export function GiftStepperSidebar({
  step,
  stepsList,
  onStepClick,
  supportUrl,
}: {
  step: GiftStep;
  stepsList: StepInfo[];
  onStepClick: (id: GiftStep) => void;
  supportUrl?: string;
}) {
  return (
    <div className="w-full md:w-80 lg:w-85 shrink-0 bg-card border-b md:border-b-0 md:border-r border-border md:flex md:flex-col md:h-full">
      {/* Scrollable independently of the footer below: min-h-0 overrides the
          flex item's default min-height:auto, which would otherwise let this
          grow to fit every step instead of respecting the bounded height and
          scrolling under it. */}
      <div className="flex flex-col flex-1 min-h-0 md:overflow-y-auto p-4 sm:p-6 lg:p-8 pb-0">
        <div>
          <h2
            className="font-bold text-lg leading-snug tracking-tight mb-4 sm:mb-8"
            style={{ color: "var(--v2-foreground)" }}
          >
            Gift Subscription
          </h2>

          <div className="space-y-10">
            {stepsList.map((st, idx) => {
              const isDone = step > st.id;
              const isCurrent = step === st.id;
              // Once the purchase is confirmed (step 4) the flow is locked: no
              // stepping back to re-run payment, which would create a duplicate
              // gift + charge.
              const isNavigable = step < 4 && (isDone || isCurrent);

              return (
                <div key={st.id} className="relative">
                  {idx < stepsList.length - 1 && (
                    <div
                      className={cn(
                        "absolute left-3.5 top-7 w-0.5 h-12 transition-colors",
                        isDone ? "bg-action" : "bg-border",
                      )}
                    />
                  )}

                  <button
                    type="button"
                    disabled={!isNavigable}
                    onClick={() => isNavigable && onStepClick(st.id)}
                    className={cn(
                      "flex items-start gap-3.5 text-left w-full group bg-transparent",
                      isNavigable ? "cursor-pointer" : "cursor-default",
                    )}
                  >
                    <div
                      className={cn(
                        "size-7 rounded-full shrink-0 flex items-center justify-center font-bold text-xs transition-all",
                        isCurrent || isDone
                          ? "bg-action text-white"
                          : "bg-card text-muted-foreground border border-border",
                        isCurrent && "ring-4 ring-action/15",
                      )}
                    >
                      {isDone ? (
                        <Check className="size-3.5 stroke-[3]" />
                      ) : (
                        st.id
                      )}
                    </div>

                    <div className="pt-0.5 min-w-0 flex-1">
                      <p
                        className={cn(
                          "text-base font-bold tracking-tight leading-tight",
                          isCurrent || isDone
                            ? "text-foreground"
                            : "text-muted-foreground",
                        )}
                      >
                        {st.title}
                      </p>
                      <p className="text-sm truncate mt-2 text-muted-foreground">
                        {isDone ? st.subCompleted : st.subActive}
                      </p>
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Pinned to the bottom of the rail, outside the scrollable region
          above - stays visible even when the steps list needs to scroll,
          instead of scrolling away with it. pt-8 as a mobile fallback:
          below md, the sidebar isn't a flex column, so this block sat flush
          against the last stepper item without it; md:pt-0 hands spacing
          back to the scroll region's own bottom padding once that layout
          kicks in. */}
      <div className="flex-shrink-0 p-4 sm:p-6 lg:p-8 pt-8 md:pt-0">
        <div className="pt-6 sm:pt-8 border-t border-border">
          <NeedHelpCard href={supportUrl || "#"} />
        </div>
        {/* Desktop only - the header's mobile hamburger drawer already
            carries its own copy of this footer, so showing it here too
            below md would duplicate it. */}
        <div className="hidden md:block pt-4">
          <SidebarLegalFooter />
        </div>
        {/* Clears the fixed mobile checkout bar for the sidebar (steps 1-2 only) */}
        {(step === 1 || step === 2) && (
          <div
            className="hidden md:block lg:hidden"
            style={{ height: 132 }}
            aria-hidden="true"
          />
        )}
      </div>
    </div>
  );
}
